import {
  House, Users,
  Calendar, Layers,
  MessageSquare, GitBranch, Megaphone, BarChart3,
  Settings, type LucideIcon
} from 'lucide-react'
import type { CapabilityName } from '@/lib/permissions'

export interface NavItem {
  label: string
  path: string
  icon: LucideIcon
  badge?: number
  /** Items with the same group sit together; a gap separates groups. */
  group: 'day' | 'work' | 'grow' | 'admin'
  /**
   * What the person must be able to do to see this item. Hidden otherwise,
   * so a crew member never taps into a "You don't have access" page.
   * Omit for items everyone sees (Home; Settings shows personal sections
   * to everyone).
   */
  capability?: CapabilityName
}

// Grouped by how the day runs: what's happening (Home, Inbox, Calendar),
// the work itself (Work, Contacts), then growing the business. The desktop
// sidebar puts the 'admin' group (Settings) in its footer.
export const navItems: NavItem[] = [
  { label: 'Home',         path: '/dashboard',    icon: House,         group: 'day' },
  { label: 'Inbox',        path: '/inbox',        icon: MessageSquare, group: 'day', capability: 'viewInbox' },
  { label: 'Calendar',     path: '/calendar',     icon: Calendar,      group: 'day', capability: 'viewCalendar' },
  { label: 'Work',         path: '/work',         icon: Layers,        group: 'work', capability: 'viewWork' },
  // Contacts (glossary §5): customers, contractors and company contacts. The
  // route stays /customers so links already out there keep working.
  { label: 'Contacts',     path: '/customers',    icon: Users,         group: 'work', capability: 'viewContacts' },
  { label: 'Workflows',    path: '/workflows',    icon: GitBranch,     group: 'grow', capability: 'viewWorkflows' },
  { label: 'Marketing',    path: '/marketing',    icon: Megaphone,     group: 'grow', capability: 'viewMarketing' },
  { label: 'Reports',      path: '/reports',      icon: BarChart3,     group: 'grow', capability: 'viewReports' },
  { label: 'Settings',     path: '/settings',     icon: Settings,      group: 'admin' },
]

/** The nav items this person can open, in order. */
export function visibleNavItems<T extends { capability?: CapabilityName }>(
  items: readonly T[],
  can: (c: CapabilityName) => boolean,
): T[] {
  return items.filter((i) => !i.capability || can(i.capability))
}

/**
 * Settings sections → what you need to see them (F1 keys). Personal sections
 * (profile, notifications, security) are open to everyone. Used by the
 * Settings page list and by the shell to decide whether any admin section
 * exists for this person.
 */
export const SETTINGS_SECTION_CAPABILITY: Record<string, CapabilityName | undefined> = {
  profile: undefined,
  notifications: undefined,
  security: undefined,
  usage: 'viewUsage', // spend is money: not for Crew
  availability: 'schedule.view', // your own hours + calendar privacy: personal, Crew included
  calendar: 'schedule.view',
  business: 'settings.business',
  brand: 'settings.business',
  domain: 'settings.business',
  email: 'settings.business',
  documents: 'settings.business',
  import: 'settings.business', // bulk writes and undo: admin only
  // Ask AI: Proactive, house rules, your own agent. Business-wide, so owner only like the rest of the group.
  'ask-ai': 'settings.business',
  money: 'settings.money',
  booking: 'settings.business',
  pricing: 'pricebook.manage',
  team: 'settings.team',
  // Line, calls, texting and quiet hours are tabs of this one page
  // (/settings/phone/<tab>); old /settings/a2p, /twilio and /services redirect.
  phone: 'settings.phone',
  // The AI front desk; buying calls and auto top-up are owner-only on the server.
  'ai-front-desk': 'settings.phone',
  integrations: 'settings.integrations',
  api: 'settings.integrations',
  // The BizForge plan. Owner only: the server allows built-in Admins (people,
  // never API keys); this keeps the section off for everyone without team rights.
  billing: 'settings.team',
  // The business's own Stripe account: owner only, like Billing (server: Admin people, never API keys).
  payments: 'settings.team',
}

export function canSeeSettingsSection(id: string, can: (c: CapabilityName) => boolean): boolean {
  const cap = SETTINGS_SECTION_CAPABILITY[id]
  return !cap || can(cap)
}

/** The phone dock (BottomTabBar) tabs, gated the same way as the sidebar. */
export const DOCK_TAB_CAPABILITY: Record<string, CapabilityName | undefined> = {
  '/work': 'viewWork',
  '/customers': 'viewContacts',
  '/dashboard': undefined,
  '/inbox': 'viewInbox',
  '/calendar': 'viewCalendar',
}
